import { useRef, useLayoutEffect, RefObject } from 'react';
import { animated, useSpring } from 'react-spring';
import { CoverImage, Image, Text } from '@belong/ui';
import clsx from 'clsx';
import numeral from 'numeral';
import css from './homeowners-trust-belong-section.module.css';

const onScreenThreshold = 0.25; // Start animation when this % of the section is on screen
const percentageLeft = 0.75 - onScreenThreshold; // Animation will happen on the remaining %

function getRect(ref: RefObject<HTMLDivElement>): DOMRect | undefined {
  return ref.current?.getBoundingClientRect();
}

// Val starts at 0 when the animation starts and ends at heightLeft when the animations ends
const scrollInterpolation =
  (ref: RefObject<HTMLDivElement>) => (val: number) => {
    const rect = getRect(ref);
    if (rect) {
      const animationHeight = rect.height;
      const heightLeft = animationHeight * percentageLeft; // The height over which the animation will happen (40% of height).
      // Result is 0 when val is 0 and goes to 1 when val is heightLeft
      const result = val / heightLeft;

      if (!result || result < 0) return 0;
      if (result >= 1) return 1;
      return result;
    }
    return 0;
  };

const twClassNames = {
  neonContainer: 'absolute inset-0 flex justify-center mx-auto',
  neonElementsContainer:
    'relative flex justify-center w-full h-full mt-0 md:mt-2xl md:mt-0',
  neonTextBase: 'absolute md:pt-4xl',
  neonTextOn: 'text-white opacity-90',
};

export type HomeownersTrustBelongSectionProps = {
  homeownersCount: number;
};

export function HomeownersTrustBelongSection({
  homeownersCount,
}: HomeownersTrustBelongSectionProps): JSX.Element {
  const hoCountWithCommas = numeral(homeownersCount).format('0,0');

  const animationRef: RefObject<HTMLDivElement> = useRef(null);
  const [{ sy }, set] = useSpring(() => ({ sy: 0 }));

  useLayoutEffect(() => {
    function handleScroll() {
      const rect = getRect(animationRef);
      if (rect) {
        const windowHeight = window.innerHeight;
        const { top: animationTop, height: animationHeight } = rect;
        const startHeight = animationHeight * onScreenThreshold;
        set({ sy: windowHeight - animationTop - startHeight });
      }
    }
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [set]);

  return (
    <div
      ref={animationRef}
      className="mb-5xl md:mb-6xl relative flex w-full flex-col items-center overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0F2532 72.16%, #102633 92.42%)',
      }}
    >
      <div className={clsx(css.coverImageBox, 'pt-2xl')}>
        <CoverImage
          src="/referral/ho_number_bg_city.svg"
          // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
          maxHeight={css.coverImageBox}
          maxWidth={2000}
          alt=""
        />
      </div>
      <div className={clsx(twClassNames.neonContainer, css.neonContainer)}>
        <div className={twClassNames.neonElementsContainer}>
          <Text
            className={clsx(
              twClassNames.neonTextBase,
              css.neonTextOff,
              css.neonTextBase,
            )}
          >
            {hoCountWithCommas}
          </Text>
          <div className="absolute inset-0 flex items-center">
            <Image
              src="/homeowner-lp/neon_number_off_bg.png"
              alt={`${homeownersCount} Homeowners Trust Belong And Counting`}
              className="h-auto"
            />
          </div>
        </div>
      </div>

      <animated.div
        className="absolute inset-0 flex w-full justify-center"
        style={{
          opacity: sy.to(scrollInterpolation(animationRef)),
        }}
      >
        <animated.div
          className={clsx(twClassNames.neonContainer, css.neonContainer)}
          style={{
            opacity: sy.to((val): number => {
              const result = scrollInterpolation(animationRef)(val);

              // Flashing of neon sign as shown in animation
              if (result > 0.8 && result < 0.85) return 0;
              if (result > 0.9 && result < 0.95) return 0;
              return result;
            }),
          }}
        >
          <div className={twClassNames.neonElementsContainer}>
            <div className="absolute inset-0 flex items-center">
              <Image
                src="/homeowner-lp/neon_number_on_bg.png"
                alt=""
                className="h-auto"
              />
            </div>
            <Text
              className={clsx(
                twClassNames.neonTextBase,
                twClassNames.neonTextOn,
                css.neonText,
                css.neonTextBase,
              )}
            >
              {hoCountWithCommas}
            </Text>
          </div>
        </animated.div>
      </animated.div>
    </div>
  );
}
